import type { Instance, SnapshotIn, SnapshotOut } from "mobx-state-tree";
import { onSnapshot, types } from "mobx-state-tree";

/**
 * UI state for the Building Data page (formerly the Building Data sidebar).
 *
 * Cross-route, cross-building persistence: the user's set of expanded groups
 * is stored under a single localStorage key. We persist via `onSnapshot`
 * after the store is built (see `connectPersistence` below) — mirrors the
 * pattern used in `listStore.test.ts` and keeps the MST model itself free of
 * side-effects.
 *
 * The storage key predates the sidebar→page move; the loader picks fields
 * explicitly, so old snapshots that still carry the retired `expanded` /
 * `width` fields load cleanly and shed them on the next write.
 */

const STORAGE_KEY = "building-data-widget:v1";

export const BuildingDataWidgetStore = types
  .model("BuildingDataWidgetStore", {
    /** Ids of groups the user has expanded. */
    openGroupIds: types.optional(types.array(types.string), []),
  })
  // One-shot open requests from the dashboard's actionable rows. Volatile so
  // they never persist to localStorage — a focus request is meaningful only
  // for the click that raised it. The Building Data page consumes + clears
  // them after the dashboard navigates to it.
  .volatile(() => ({
    /** Field id (attribute_nm) the page should scroll to on arrival. */
    pendingFocusFieldId: null as string | null,
    /** Status filter the page should apply on arrival (dashboard "fields to fill" row). */
    pendingFilter: null as "empty" | "pending" | null,
  }))
  .actions((self) => ({
    toggleGroup(id: string) {
      const idx = self.openGroupIds.indexOf(id);
      if (idx >= 0) {
        self.openGroupIds.splice(idx, 1);
      } else {
        self.openGroupIds.push(id);
      }
    },
    /** Ensure a group is expanded (idempotent, unlike toggleGroup). */
    openGroup(id: string) {
      if (!self.openGroupIds.includes(id)) {
        self.openGroupIds.push(id);
      }
    },
    /** Request the page to scroll to one specific field (e.g. a conflict row click). */
    focusField(fieldId: string) {
      self.pendingFocusFieldId = fieldId;
      self.pendingFilter = null;
    },
    clearFocusField() {
      self.pendingFocusFieldId = null;
    },
    /** Request the page to apply a status filter on arrival (e.g. "fields to fill"). */
    openWithFilter(filter: "empty" | "pending") {
      self.pendingFilter = filter;
      self.pendingFocusFieldId = null;
    },
    clearPendingFilter() {
      self.pendingFilter = null;
    },
    reset() {
      self.openGroupIds.clear();
      self.pendingFocusFieldId = null;
      self.pendingFilter = null;
    },
  }));

export type BuildingDataWidgetStoreInstance = Instance<typeof BuildingDataWidgetStore>;
export type BuildingDataWidgetStoreSnapshotIn = SnapshotIn<typeof BuildingDataWidgetStore>;
export type BuildingDataWidgetStoreSnapshotOut = SnapshotOut<typeof BuildingDataWidgetStore>;

/**
 * Read the initial snapshot for the widget store from localStorage.
 * Safe to call in non-browser environments — falls back to defaults.
 */
export function loadBuildingDataWidgetSnapshot(): BuildingDataWidgetStoreSnapshotIn {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return {};
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Partial<BuildingDataWidgetStoreSnapshotIn>;
    return {
      openGroupIds: Array.isArray(parsed.openGroupIds)
        ? (parsed.openGroupIds.filter((id) => typeof id === "string") as string[])
        : undefined,
    };
  } catch {
    return {};
  }
}

/**
 * Subscribe to widget-store snapshots and mirror them into localStorage.
 * Call once after the root store has been created.
 *
 * Writes are coalesced with a 200ms trailing debounce so a burst of group
 * toggles doesn't pin the main thread on synchronous JSON.stringify +
 * localStorage.setItem. `beforeunload` flushes the latest snapshot so a
 * value mid-debounce isn't lost when the tab closes.
 */
export function connectBuildingDataWidgetPersistence(
  store: BuildingDataWidgetStoreInstance
): () => void {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return () => undefined;
  }

  let pending: BuildingDataWidgetStoreSnapshotOut | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const flush = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (!pending) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
    } catch {
      // Quota / privacy-mode failures are non-fatal for a purely-UI preference.
    }
    pending = null;
  };

  const dispose = onSnapshot(store, (snapshot) => {
    pending = snapshot;
    if (timer) return;
    timer = setTimeout(flush, 200);
  });

  window.addEventListener("beforeunload", flush);

  return () => {
    flush();
    window.removeEventListener("beforeunload", flush);
    dispose();
  };
}
